import React from "react";
import { Badge, Text, VStack } from "@chakra-ui/react";
import { ViewControl } from "../ViewControl/ViewControl";

type FilterSummaryVariant = "default" | "compact";

type FilterSummaryProps = {
  queryString: string;
  variant?: FilterSummaryVariant;
};

const formatLabel = (label: string, variant: FilterSummaryVariant) => {
  const formatted = label.replace(/([a-z])([A-Z])/g, "$1 $2");
  return variant === "compact"
    ? formatted.replace(/\b\w/g, (c) => c.toUpperCase())
    : formatted;
};

const parseQuery = (queryString: string) => {
  const params = new URLSearchParams(queryString);
  const summary: Record<string, { include: string[]; exclude: string[] }> = {};

  for (const [key, value] of Array.from(params.entries()) as [
    string,
    string
  ][]) {
    const targetKey = key.trim();
    const isExclusion = value.startsWith("!");
    let actualValue = isExclusion ? value.slice(1) : value;

    if (!summary[targetKey]) summary[targetKey] = { include: [], exclude: [] };

    if (isExclusion) {
      summary[targetKey].exclude.push(actualValue);
    } else if (actualValue) {
      actualValue =
        actualValue === "true"
          ? "Yes"
          : actualValue === "false"
          ? "No"
          : actualValue;
      summary[targetKey].include.push(actualValue);
    }
  }

  return summary;
};

const renderBadges = (values: string[]) =>
  values.map((val, idx) => (
    <Badge
      key={idx}
      background={"transparent"}
      border={"1px solid"}
      borderColor={"gray.300"}
      mr={2}
      borderRadius={7}
    >
      {val}
    </Badge>
  ));

const renderValues = (
  title: string,
  values: string[],
  variant: FilterSummaryVariant
) => (
  <ViewControl
    label={formatLabel(title, variant)}
    labelProps={
      variant === "compact"
        ? { flex: 1, color: "unset", textTransform: "unset" }
        : { flex: 1 }
    }
    valueProps={{ flex: 1 }}
    value={renderBadges(values)}
  />
);

const renderExclusions = (values: string[], variant: string) =>
  values.length > 0 && (
    <ViewControl
      label={"Excluding:"}
      labelProps={
        variant === "compact"
          ? { flex: 1, textTransform: "unset" }
          : { flex: 1 }
      }
      valueProps={{ flex: 1 }}
      value={renderBadges(values)}
      mt={"4px !important"}
    />
  );

export const MembershipFilterSummary: React.FC<FilterSummaryProps> = ({
  queryString,
  variant = "default",
}) => {
  const summary = parseQuery(queryString);
  const hasFilters = Object.values(summary).some(
    ({ include, exclude }) => include.length > 0 || exclude.length > 0
  );

  const spacing = variant === "compact" ? 3 : 6;

  return (
    <VStack align="start" w="100%" spacing={spacing}>
      {hasFilters ? (
        Object.entries(summary).map(([key, { include, exclude }]) =>
          include.length > 0 || exclude.length > 0 ? (
            <>
              {renderValues(key + ":", include, variant)}
              {renderExclusions(exclude, variant)}
            </>
          ) : null
        )
      ) : (
        <Text>No filters configured</Text>
      )}
    </VStack>
  );
};
